import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, formatDate } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';

type Role = { role: string; label: string; status: string; is_valid: boolean; valid_until: string | null };

type Party = {
  id: number;
  type: 'pessoa_fisica' | 'pessoa_juridica';
  name: string;
  legal_name: string | null;
  trade_name: string | null;
  document: string | null;
  document_type: string | null;
  document_is_masked: boolean;
  status: string;
  default_payment_term_days: number | null;
  notes: string | null;
  roles: Role[];
  active_roles: string[];
};

type PartyDetail = Party & {
  contacts: Array<{ id: number; type: string; value: string; label: string | null; is_primary: boolean }>;
  addresses: Array<{ id: number; purpose: string; one_line: string }>;
  financials: {
    open_balance: MoneyString;
    total_transacted: MoneyString;
    transaction_count: number;
    last_transaction_date: string | null;
    average_delay_days: number | null;
    settlements_analyzed: number;
  };
};

const ROLE_LABELS: Record<string, string> = {
  cliente: 'Cliente',
  fornecedor: 'Fornecedor',
  funcionario: 'Funcionário',
  lead: 'Lead',
  prospect: 'Prospect',
  credor: 'Credor',
  devedor: 'Devedor',
  socio: 'Sócio',
  transportadora: 'Transportadora',
  parceiro: 'Parceiro',
};

export function PartiesPage({ selectedIds }: { selectedIds: number[] }) {
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Party | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const params = new URLSearchParams();
  if (roleFilter) params.set('role', roleFilter);
  if (search) params.set('q', search);

  const list = useQuery({
    queryKey: ['parties', params.toString()],
    queryFn: () =>
      api.get<{ data: Party[]; meta: { total: number } }>(`/parties?${params}`, {
        entityIds: selectedIds,
      }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete<{ message: string }>(`/parties/${id}`, { entityIds: selectedIds }),
    onSuccess: (res) => {
      if (res.message) window.alert(res.message);
      void queryClient.invalidateQueries({ queryKey: ['parties'] });
    },
  });

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Clientes e fornecedores</h1>
        <button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
        >
          + Novo cadastro
        </button>
      </div>

      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Um cadastro por pessoa ou empresa, com quantos papéis forem necessários. A mesma pessoa pode
        ser cliente e fornecedor ao mesmo tempo, sem cadastro duplicado.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="input py-1.5 text-sm"
        >
          <option value="">Todos os papéis</option>
          {Object.entries(ROLE_LABELS).map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
        <input
          type="search"
          placeholder="nome, razão social ou documento"
          defaultValue={search}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearch((e.target as HTMLInputElement).value);
          }}
          className="input w-64 py-1.5 text-sm"
        />
        <span className="ml-auto text-sm text-slate-500 dark:text-slate-400">
          {list.data?.meta.total ?? 0} cadastro(s)
        </span>
      </div>

      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {list.isPending ? (
          <div className="animate-pulse p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="mb-2 h-9 rounded bg-slate-100 dark:bg-slate-800" />
            ))}
          </div>
        ) : list.isError ? (
          <p className="p-8 text-center text-sm text-rose-600">Erro ao carregar.</p>
        ) : list.data.data.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium">Nenhum cadastro encontrado</p>
            <button
              onClick={() => { setEditing(null); setFormOpen(true); }}
              className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              + Novo cadastro
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2.5 font-medium">Nome</th>
                <th className="px-3 py-2.5 font-medium">Documento</th>
                <th className="px-3 py-2.5 font-medium">Papéis</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.data.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-3 py-2.5">
                    <span className="font-medium">{p.name}</span>
                    <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">
                      {p.type === 'pessoa_juridica' ? 'PJ' : 'PF'}
                    </span>
                    {p.legal_name && p.legal_name !== p.name && (
                      <span className="block text-xs text-slate-400">{p.legal_name}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600 dark:text-slate-300">
                    {p.document ?? '—'}
                    {p.document && p.document_is_masked && (
                      <span className="ml-1 text-[10px] text-slate-400" title="Você não tem permissão para ver o documento completo">
                        oculto
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {p.active_roles.map((r) => (
                        <span
                          key={r}
                          className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        >
                          {ROLE_LABELS[r] ?? r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right">
                    <button
                      onClick={() => setOpenId(openId === p.id ? null : p.id)}
                      className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
                    >
                      {openId === p.id ? 'Fechar' : 'Ficha'}
                    </button>
                    <button
                      onClick={() => { setEditing(p); setFormOpen(true); }}
                      className="ml-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-indigo-600"
                    >
                      editar
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Excluir "${p.name}"?`)) remove.mutate(p.id);
                      }}
                      className="ml-1 rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-rose-600"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId !== null && <PartyDetailPanel partyId={openId} selectedIds={selectedIds} />}

      {formOpen && (
        <PartyForm
          party={editing}
          selectedIds={selectedIds}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['parties'] });
            void queryClient.invalidateQueries({ queryKey: ['reference'] });
          }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function PartyDetailPanel({ partyId, selectedIds }: { partyId: number; selectedIds: number[] }) {
  const detail = useQuery({
    queryKey: ['party', partyId, selectedIds],
    queryFn: () => api.get<{ data: PartyDetail }>(`/parties/${partyId}`, { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  if (detail.isPending) {
    return <div className="mt-6 h-40 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />;
  }
  if (detail.isError) return <p className="mt-6 text-sm text-rose-600">Erro ao carregar a ficha.</p>;

  const p = detail.data;
  const f = p.financials;

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <h2 className="font-medium">{p.name}</h2>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Em aberto" value={formatMoney(f.open_balance)} />
        <Stat label="Já transacionado" value={formatMoney(f.total_transacted)} />
        <Stat label="Lançamentos" value={String(f.transaction_count)} />
        <Stat
          label="Atraso médio"
          value={f.average_delay_days === null ? '—' : `${f.average_delay_days} dias`}
          hint={f.settlements_analyzed > 0 ? `${f.settlements_analyzed} liquidações` : 'sem histórico'}
        />
      </div>

      {f.last_transaction_date && (
        <p className="mt-3 text-xs text-slate-400">
          Último lançamento em {formatDate(f.last_transaction_date)}
        </p>
      )}

      {p.contacts.length > 0 && (
        <>
          <h3 className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Contatos
          </h3>
          <ul className="mt-2 space-y-1 text-sm">
            {p.contacts.map((c) => (
              <li key={c.id}>
                <span className="text-slate-500 dark:text-slate-400">{c.type}: </span>
                {c.value}
                {c.label && <span className="ml-1 text-xs text-slate-400">({c.label})</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {p.addresses.length > 0 && (
        <>
          <h3 className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Endereço
          </h3>
          <p className="mt-1 text-sm">{p.addresses[0]?.one_line}</p>
        </>
      )}

      {/* Papéis encerrados aparecem riscados: o histórico continua visível
          porque explica lançamentos antigos. */}
      <h3 className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Papéis
      </h3>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {p.roles.map((r, i) => (
          <span
            key={i}
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              r.is_valid
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                : 'bg-slate-100 text-slate-400 line-through dark:bg-slate-800'
            }`}
            title={r.valid_until ? `encerrado em ${formatDate(r.valid_until)}` : undefined}
          >
            {r.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-0.5 font-semibold">{value}</p>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

// -------------------------------------------------------------------

function PartyForm({
  party,
  selectedIds,
  onClose,
  onSaved,
}: {
  party: Party | null;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [type, setType] = useState(party?.type ?? 'pessoa_juridica');
  const [name, setName] = useState(party?.name ?? '');
  const [legalName, setLegalName] = useState(party?.legal_name ?? '');
  const [document, setDocument] = useState(party?.document_is_masked ? '' : (party?.document ?? ''));
  const [roles, setRoles] = useState<string[]>(party?.active_roles ?? ['fornecedor']);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [paymentTerm, setPaymentTerm] = useState<number | ''>(party?.default_payment_term_days ?? '');

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        type,
        name,
        ...(legalName ? { legal_name: legalName } : {}),
        ...(document ? { document } : {}),
        roles,
        ...(paymentTerm !== '' ? { default_payment_term_days: Number(paymentTerm) } : {}),
        ...(!party && (email || phone)
          ? {
              contacts: [
                ...(email ? [{ type: 'email', value: email }] : []),
                ...(phone ? [{ type: 'telefone', value: phone }] : []),
              ],
            }
          : {}),
      };

      return party
        ? api.patch(`/parties/${party.id}`, payload, { entityIds: selectedIds })
        : api.post('/parties', payload, { entityIds: selectedIds });
    },
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;
  const canSubmit = name.trim().length >= 2 && roles.length > 0;

  function toggleRole(r: string) {
    setRoles((current) => (current.includes(r) ? current.filter((x) => x !== r) : [...current, r]));
  }

  return (
    <Modal title={party ? 'Editar cadastro' : 'Novo cadastro'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(error.errors).map(([f, m]) => <li key={f}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="flex gap-2">
          {(['pessoa_juridica', 'pessoa_fisica'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition ${
                type === t
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300'
                  : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400'
              }`}
            >
              {t === 'pessoa_juridica' ? 'Pessoa jurídica' : 'Pessoa física'}
            </button>
          ))}
        </div>

        <Field label={type === 'pessoa_juridica' ? 'Nome fantasia' : 'Nome'} required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoFocus
            className="input w-full"
          />
        </Field>

        {type === 'pessoa_juridica' && (
          <Field label="Razão social">
            <input value={legalName} onChange={(e) => setLegalName(e.target.value)} className="input w-full" />
          </Field>
        )}

        <Field
          label={type === 'pessoa_juridica' ? 'CNPJ' : 'CPF'}
          hint={party?.document_is_masked ? 'deixe vazio para manter o atual' : 'com ou sem máscara'}
        >
          <input
            value={document}
            onChange={(e) => setDocument(e.target.value)}
            placeholder={type === 'pessoa_juridica' ? '00.000.000/0000-00' : '000.000.000-00'}
            className="input w-full"
          />
        </Field>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Papéis<span className="ml-0.5 text-rose-500">*</span>
          </span>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(ROLE_LABELS).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => toggleRole(v)}
                className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition ${
                  roles.includes(v)
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                    : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="mt-1 block text-xs text-slate-400">
            Pode marcar mais de um. Papel removido é encerrado, não apagado — o histórico continua
            explicável.
          </span>
        </div>

        {!party && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="E-mail">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input w-full" />
            </Field>
            <Field label="Telefone">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input w-full" />
            </Field>
          </div>
        )}

        <Field label="Prazo de pagamento padrão" hint="dias, para sugerir o vencimento">
          <input
            type="number"
            min={0}
            max={365}
            value={paymentTerm}
            onChange={(e) => setPaymentTerm(e.target.value === '' ? '' : Number(e.target.value))}
            className="input w-full text-right"
          />
        </Field>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
